import React, { useState, useEffect, useRef } from 'react';
import SectionInfoBox from '../../../components/SectionInfoBox';
import { getEmbedUrl, isDirectVideoFile, isDummyUrl } from '../../../utils/mediaUtils';


interface FloorPlansSectionProps {
    project: any;
    expandedFloorPlan: string | null;
    setExpandedFloorPlan: (id: string | null) => void;
    getLabelIcon: (label: string) => React.ReactNode;
    getImageUrl: (url: string) => string;
    setZoomedImage: (url: string | null) => void;
}

const FloorPlansSection: React.FC<FloorPlansSectionProps> = ({
    project,
    expandedFloorPlan,
    setExpandedFloorPlan,
    getLabelIcon,
    getImageUrl,
    setZoomedImage
}) => {
    const floorPlans = React.useMemo(() => {
        if (!project.floorPlans || project.floorPlans.length === 0) return [];
        if (project.floorPlans.length > 1) return project.floorPlans;

        const single = project.floorPlans[0];
        const subUnits = single.subUnits || [];
        const isCombinedType = single.unitType && (single.unitType.includes(',') || single.unitType.includes('/'));
        const hasDistinctBhk = subUnits.some((su: any) => /([1-9])\s*bhk/i.test(`${su.subUnitVariant || ''} ${su.subUnitType || ''} ${su.image || ''}`));

        if ((isCombinedType || hasDistinctBhk) && subUnits.length > 1) {
            const grouped: { [key: string]: any[] } = {};
            subUnits.forEach((su: any) => {
                const text = `${su.subUnitVariant || ''} ${su.subUnitType || ''} ${su.image || ''}`;
                const m = text.match(/([1-9])\s*bhk/i);
                let uType = single.unitType || 'Standard';
                if (m) {
                    uType = `${m[1]} BHK`;
                } else if (/penthouse/i.test(text)) {
                    uType = 'Penthouse';
                } else if (/villa/i.test(text)) {
                    uType = 'Villa';
                }
                if (!grouped[uType]) grouped[uType] = [];
                grouped[uType].push(su);
            });

            if (Object.keys(grouped).length > 1) {
                return Object.entries(grouped).map(([uType, items]) => ({
                    unitType: uType,
                    priceRange: single.priceRange || 'On Request',
                    subUnits: items
                }));
            }
        }

        return project.floorPlans;
    }, [project.floorPlans]);

    const [activeTab, setActiveTab] = useState(0);
    const [activeSubTab, setActiveSubTab] = useState(0);
    const [viewMode, setViewMode] = useState<'2d' | '3d' | 'info' | 'virtual'>('2d');

    useEffect(() => {
        setActiveSubTab(0);
        setViewMode('2d');
    }, [activeTab]);

    useEffect(() => {
        setViewMode('2d');
    }, [activeSubTab]);
    const scrollRef = useRef<HTMLDivElement>(null);
    const scrollRefSecondary = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [canScrollLeftSec, setCanScrollLeftSec] = useState(false);
    const [canScrollRightSec, setCanScrollRightSec] = useState(false);

    const checkScroll = (ref: React.RefObject<HTMLDivElement>, setLeft: Function, setRight: Function) => {
        if (ref.current) {
            const { scrollLeft, scrollWidth, clientWidth } = ref.current;
            setLeft(scrollLeft > 5);
            setRight(scrollLeft + clientWidth < scrollWidth - 5);
        }
    };

    useEffect(() => {
        const el = scrollRef.current;
        const el2 = scrollRefSecondary.current;
        const checkAll = () => {
            checkScroll(scrollRef, setCanScrollLeft, setCanScrollRight);
            checkScroll(scrollRefSecondary, setCanScrollLeftSec, setCanScrollRightSec);
        };
        
        if (el) el.addEventListener('scroll', checkAll);
        if (el2) el2.addEventListener('scroll', checkAll);
        window.addEventListener('resize', checkAll);
        
        checkAll();
        return () => {
            if (el) el.removeEventListener('scroll', checkAll);
            if (el2) el2.removeEventListener('scroll', checkAll);
            window.removeEventListener('resize', checkAll);
        };
    }, [floorPlans, activeTab]);

    const scroll = (ref: React.RefObject<HTMLDivElement>, direction: 'left' | 'right') => {
        if (ref.current) {
            const { scrollLeft, clientWidth } = ref.current;
            const scrollAmount = clientWidth * 0.8;
            ref.current.scrollTo({
                left: direction === 'left' ? scrollLeft - scrollAmount : scrollLeft + scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    if (!floorPlans || floorPlans.length === 0) return null;

    const currentUnit = floorPlans[activeTab] || floorPlans[0];
    const currentSubUnit = currentUnit?.subUnits?.[activeSubTab] || currentUnit?.subUnits?.[0];

    return (
        <div id="floor-plans" className="card border border-gray-200 shadow-sm rounded-3xl mt-4 mb-6 bg-white overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 md:px-6 border-b border-gray-50 flex flex-col justify-between gap-3 bg-white">
                <div className="flex items-center gap-4">
                    <h4 className="text-xl md:text-2xl mb-0 font-black bg-clip-text text-transparent bg-gradient-to-r from-[#05144b] to-[#4c1d95] flex items-center gap-2">
                        {project.name} Floor Plans and Pricing
                        <SectionInfoBox text={project.sectionInfo?.floorPlans} />
                    </h4>
                </div>
            </div>

            <div className="p-0 bg-white">
                {/* Level 1 Tabs: Unit Types with Price Range */}
                <div className="px-4 md:px-6 pt-4 pb-1">
                    <div className="relative flex items-center group">
                        {canScrollLeft && (
                            <button onClick={() => scroll(scrollRef, 'left')} className="absolute left-[-20px] z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white shadow-md border border-gray-100 text-black hover:bg-gray-50">
                                <i className="fa-solid fa-chevron-left text-[10px]"></i>
                            </button>
                        )}
                        <div ref={scrollRef} className="flex gap-4 overflow-x-auto no-scrollbar pb-4 w-full">
                            {floorPlans.map((fp: any, idx: number) => (
                                <button
                                    key={idx}
                                    onClick={() => setActiveTab(idx)}
                                    className={`flex flex-col items-start px-4 py-2 sm:px-6 sm:py-3 rounded-2xl border-2 transition-all min-w-[130px] sm:min-w-[160px] text-left group ${activeTab === idx ? 'bg-gradient-to-r from-[#05144b] to-[#4c1d95] border-[#f1ae24] shadow-xl scale-[1.02]' : 'border-gray-100 hover:border-gray-300 bg-gray-50/30'}`}
                                >
                                    <span className={`text-[12px] md:text-[13px] font-black mb-1 ${activeTab === idx ? 'text-[#f1ae24]' : 'text-black/40 group-hover:text-black/60'}`}>
                                        {fp.unitType || `Phase ${idx + 1}`}
                                    </span>
                                    <span className={`text-[14px] md:text-[16px] font-black leading-none ${activeTab === idx ? 'text-white' : 'text-black'}`}>
                                        {fp.priceRange || 'On Request'}
                                    </span>
                                </button>
                            ))}
                        </div>
                        {canScrollRight && (
                            <button onClick={() => scroll(scrollRef, 'right')} className="absolute right-[-20px] z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white shadow-md border border-gray-100 text-black hover:bg-gray-50">
                                <i className="fa-solid fa-chevron-right text-[10px]"></i>
                            </button>
                        )}
                    </div>
                </div>

                {/* Level 2 Tabs: Area Variants */}
                <div className="px-4 md:px-6 border-b border-gray-50">
                    <div className="flex items-center group relative">
                         {canScrollLeftSec && (
                            <button onClick={() => scroll(scrollRefSecondary, 'left')} className="absolute left-[-15px] z-10 w-6 h-6 flex items-center justify-center text-black active:scale-95">
                                <i className="fa-solid fa-chevron-left text-[10px]"></i>
                            </button>
                        )}
                        <div ref={scrollRefSecondary} className="flex items-center gap-10 overflow-x-auto no-scrollbar py-4 scroll-smooth">
                            {currentUnit?.subUnits?.map((su: any, idx: number) => (
                                <button
                                    key={idx}
                                    onClick={() => setActiveSubTab(idx)}
                                    className={`relative py-1 text-[13px] font-bold transition-all whitespace-nowrap ${activeSubTab === idx ? 'text-black' : 'text-black/60 hover:text-black'}`}
                                >
                                {su.subUnitVariant || `${su.carpetArea || su.superBuiltUpArea || 'Variant'} SQ.FT`}
                                    {activeSubTab === idx && (
                                        <div className="absolute bottom-[-17px] left-0 w-full h-[3px] bg-gradient-to-r from-[#05144b] to-[#4c1d95] rounded-t-full"></div>
                                    )}
                                </button>
                            ))}
                        </div>
                        {canScrollRightSec && (
                            <button onClick={() => scroll(scrollRefSecondary, 'right')} className="absolute right-[-15px] z-10 w-6 h-6 flex items-center justify-center text-black active:scale-95">
                                <i className="fa-solid fa-chevron-right text-[10px]"></i>
                            </button>
                        )}
                    </div>
                </div>

                {/* Main Content Stage */}
                <div className={`p-0 transition-opacity duration-300 ${!currentSubUnit ? 'opacity-0' : 'opacity-100'}`}>
                    {currentSubUnit && (
                        <div className="w-full">
                            {/* Interactive Stage */}
                            <div className="relative overflow-hidden mb-2 min-h-[300px] flex flex-col items-center justify-center group/stage bg-gray-50/50">
                                
                                {(() => {
                                    const effective2D = currentSubUnit.image2D || currentSubUnit.image || currentUnit.subUnits?.find((su: any) => su.image2D)?.image2D;
                                    const effective3D = currentSubUnit.image3D || currentUnit.subUnits?.find((su: any) => su.image3D)?.image3D || effective2D;
                                    
                                    const rawVirtual = currentSubUnit.virtual3DLink || 
                                        project.virtualTour360 || 
                                        project.virtualTourLink || 
                                        project.virtualTours?.[0]?.link || 
                                        project.google3dViewLink || 
                                        project.mapDetails?.google3dViewLink;

                                    const isVirtualVideo = !isDummyUrl(rawVirtual) && isDirectVideoFile(rawVirtual);
                                    const effectiveVirtual = !isDummyUrl(rawVirtual) ? (isVirtualVideo ? rawVirtual : getEmbedUrl(rawVirtual)) : '';

                                    const effectiveInfo = currentSubUnit.infoImage || 
                                        (currentSubUnit.subUnitVariant?.toLowerCase().includes('info') ? (currentSubUnit.image2D || currentSubUnit.image) : '') ||
                                        currentUnit.subUnits?.find((su: any) => su.infoImage)?.infoImage ||
                                        currentUnit.subUnits?.find((su: any) => su.subUnitVariant?.toLowerCase().includes('info'))?.image;

                                    return (
                                        <>
                                            {/* Golden Pill Vertical Toggle */}
                                            <div className="absolute top-4 right-4 z-30 flex flex-col items-center justify-between gap-2 bg-[#EFA327] py-2 px-1 rounded-full shadow-lg w-[46px] select-none">
                                                {/* VR Button */}
                                                <button
                                                    type="button"
                                                    onClick={() => setViewMode('virtual')}
                                                    className={`w-[38px] h-[38px] rounded-full flex items-center justify-center transition-all duration-200 relative ${
                                                        viewMode === 'virtual'
                                                            ? 'bg-[#151246] text-white shadow-md'
                                                            : 'text-[#0c153b] hover:bg-black/10'
                                                    }`}
                                                    title={effectiveVirtual ? "Virtual 3D / VR Tour" : "360° Virtual Walkthrough"}
                                                >
                                                    <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                                                        <path d="M20.5 6h-17C2.1 6 1 7.1 1 8.5v7C1 16.9 2.1 18 3.5 18h4.2c.4 0 .8-.2 1.1-.5l2.2-2.5h2l2.2 2.5c.3.3.7.5 1.1.5h4.2c1.4 0 2.5-1.1 2.5-2.5v-7C23 7.1 21.9 6 20.5 6zM6.5 14C5.1 14 4 12.9 4 11.5S5.1 9 6.5 9 9 10.1 9 11.5 7.9 14 6.5 14zm11 0c-1.4 0-2.5-1.1-2.5-2.5s1.1-2.5 2.5-2.5 2.5 1.1 2.5 2.5-1.1 2.5-2.5 2.5z"/>
                                                    </svg>
                                                    {effectiveVirtual && viewMode !== 'virtual' && (
                                                        <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-blue-500 ring-2 ring-[#EFA327]"></span>
                                                    )}
                                                </button>

                                                {/* 3D Button */}
                                                <button
                                                    type="button"
                                                    onClick={() => setViewMode('3d')}
                                                    className={`w-[38px] h-[38px] rounded-full flex items-center justify-center transition-all duration-200 ${
                                                        viewMode === '3d'
                                                            ? 'bg-[#151246] text-white shadow-md'
                                                            : 'text-[#0c153b] hover:bg-black/10'
                                                    }`}
                                                    title="3D Floor Plan"
                                                >
                                                    <span className="text-[13px] font-black tracking-tight leading-none">3D</span>
                                                </button>

                                                {/* 2D Button */}
                                                <button
                                                    type="button"
                                                    onClick={() => setViewMode('2d')}
                                                    className={`w-[38px] h-[38px] rounded-full flex items-center justify-center transition-all duration-200 ${
                                                        viewMode === '2d'
                                                            ? 'bg-[#151246] text-white shadow-md'
                                                            : 'text-[#0c153b] hover:bg-black/10'
                                                    }`}
                                                    title="2D Floor Plan"
                                                >
                                                    <span className="text-[13px] font-black tracking-tight leading-none">2D</span>
                                                </button>

                                                {/* Info Button */}
                                                <button
                                                    type="button"
                                                    onClick={() => setViewMode('info')}
                                                    className={`w-[38px] h-[38px] rounded-full flex items-center justify-center transition-all duration-200 relative ${
                                                        viewMode === 'info'
                                                            ? 'bg-[#151246] text-white shadow-md'
                                                            : 'text-[#0c153b] hover:bg-black/10'
                                                    }`}
                                                    title={effectiveInfo ? "Unit Info Blueprint" : "Unit Info"}
                                                >
                                                    <span className="font-serif font-black text-[17px] leading-none">i</span>
                                                    {effectiveInfo && viewMode !== 'info' && (
                                                        <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-[#EFA327]"></span>
                                                    )}
                                                </button>
                                            </div>

                                            {/* Variation Navigation arrows */}
                                            <div className="absolute top-1/2 left-2 -translate-y-1/2 z-20 hidden sm:block">
                                                <button 
                                                    onClick={() => activeSubTab > 0 && setActiveSubTab(activeSubTab - 1)}
                                                    className={`w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-xl border border-gray-100 text-black transition-all active:scale-90 ${activeSubTab === 0 ? 'opacity-30 pointer-events-none' : 'hover:bg-gray-50'}`}
                                                >
                                                    <i className="fa-solid fa-arrow-left text-xs"></i>
                                                </button>
                                            </div>
                                            <div className="absolute top-1/2 right-2 -translate-y-1/2 z-20 hidden sm:block">
                                                <button 
                                                    onClick={() => activeSubTab < (currentUnit.subUnits?.length || 0) - 1 && setActiveSubTab(activeSubTab + 1)}
                                                    className={`w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-xl border border-gray-100 text-black transition-all active:scale-90 ${activeSubTab === (currentUnit.subUnits?.length || 0) - 1 ? 'opacity-30 pointer-events-none' : 'hover:bg-gray-50'}`}
                                                >
                                                    <i className="fa-solid fa-arrow-right text-xs"></i>
                                                </button>
                                            </div>

                                            {/* Main Interactive Stage Views */}
                                            {viewMode === 'virtual' ? (
                                                effectiveVirtual ? (
                                                    <div className="w-full h-[400px] md:h-[500px] relative p-2 bg-black rounded-2xl flex items-center justify-center overflow-hidden">
                                                        {isVirtualVideo ? (
                                                            <video
                                                                src={getImageUrl(effectiveVirtual)}
                                                                controls
                                                                playsInline
                                                                className="w-full h-full object-contain rounded-2xl shadow-inner"
                                                            ></video>
                                                        ) : (
                                                            <iframe
                                                                src={effectiveVirtual}
                                                                className="w-full h-full border-0 rounded-2xl shadow-inner"
                                                                allowFullScreen
                                                                allow="xr-spatial-tracking; gyroscope; accelerometer; fullscreen"
                                                            ></iframe>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="w-full min-h-[380px] md:min-h-[460px] flex flex-col items-center justify-center p-8 bg-gradient-to-br from-[#05144b] to-[#1e1b4b] text-white rounded-2xl text-center">
                                                        <div className="w-16 h-16 rounded-2xl bg-[#f1ae24]/20 border border-[#f1ae24]/40 flex items-center justify-center mb-4 text-[#f1ae24] text-2xl shadow-inner animate-pulse">
                                                            <svg className="w-9 h-9 fill-current" viewBox="0 0 24 24">
                                                                <path d="M20.5 6h-17C2.1 6 1 7.1 1 8.5v7C1 16.9 2.1 18 3.5 18h4.2c.4 0 .8-.2 1.1-.5l2.2-2.5h2l2.2 2.5c.3.3.7.5 1.1.5h4.2c1.4 0 2.5-1.1 2.5-2.5v-7C23 7.1 21.9 6 20.5 6zM6.5 14C5.1 14 4 12.9 4 11.5S5.1 9 6.5 9 9 10.1 9 11.5 7.9 14 6.5 14zm11 0c-1.4 0-2.5-1.1-2.5-2.5s1.1-2.5 2.5-2.5 2.5 1.1 2.5 2.5-1.1 2.5-2.5 2.5z"/>
                                                            </svg>
                                                        </div>
                                                        <h5 className="text-xl font-black text-white mb-2">360° Virtual Walkthrough</h5>
                                                        <p className="text-sm text-gray-300 max-w-md mb-6">
                                                            Interactive 3D Virtual Tour for <span className="font-bold text-[#f1ae24]">{currentSubUnit.subUnitVariant || currentUnit.unitType}</span> is currently being prepared.
                                                        </p>
                                                        <div className="flex flex-wrap gap-3 justify-center">
                                                            <button
                                                                type="button"
                                                                onClick={() => setViewMode('3d')}
                                                                className="px-5 py-2.5 rounded-xl bg-[#f1ae24] text-[#05144b] font-bold text-xs uppercase tracking-wider shadow-lg hover:bg-yellow-400 transition-all active:scale-95 flex items-center gap-2"
                                                            >
                                                                <i className="fa-solid fa-cube text-xs"></i>
                                                                View 3D Floor Plan
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => setViewMode('2d')}
                                                                className="px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs uppercase tracking-wider transition-all active:scale-95"
                                                            >
                                                                Back to 2D
                                                            </button>
                                                        </div>
                                                    </div>
                                                )
                                            ) : viewMode === 'info' ? (
                                                effectiveInfo ? (
                                                    /* Actual Info Image View */
                                                    <div 
                                                        className="w-full h-full max-h-[500px] flex items-center justify-center p-0 cursor-zoom-in group/canvas relative"
                                                        onClick={() => setZoomedImage(getImageUrl(effectiveInfo))}
                                                    >
                                                        <img
                                                            src={getImageUrl(effectiveInfo)}
                                                            className="w-full h-full object-contain transition-transform duration-700 group-hover/canvas:scale-105"
                                                            alt={`${currentUnit.unitType} Info Plan`}
                                                            key={`${activeTab}-${activeSubTab}-info`}
                                                        />
                                                    </div>
                                                ) : (
                                                    /* Clean placeholder if no info image uploaded yet */
                                                    <div className="w-full min-h-[380px] flex flex-col items-center justify-center p-8 text-center bg-gray-50/80 rounded-2xl">
                                                        <div className="w-14 h-14 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center mb-3 text-indigo-700 shadow-sm">
                                                            <i className="fa-solid fa-circle-info text-2xl"></i>
                                                        </div>
                                                        <h5 className="text-base font-black text-slate-800 mb-1">Unit Info / Blueprint Plan</h5>
                                                        <p className="text-xs text-slate-500 max-w-sm mb-5">
                                                            Specification layout blueprint has not been uploaded for <span className="font-bold text-slate-700">{currentSubUnit.subUnitVariant || currentUnit.unitType}</span> yet.
                                                        </p>
                                                        <button
                                                            type="button"
                                                            onClick={() => setViewMode('2d')}
                                                            className="px-5 py-2.5 rounded-xl bg-[#05144b] text-white font-bold text-xs uppercase tracking-wider hover:bg-[#4c1d95] shadow transition-all active:scale-95 flex items-center gap-2"
                                                        >
                                                            <i className="fa-solid fa-arrow-left text-[11px]"></i>
                                                            <span>Back to 2D Plan</span>
                                                        </button>
                                                    </div>
                                                )
                                            ) : (
                                                /* 2D or 3D View */
                                                <div 
                                                    className="w-full h-full max-h-[500px] flex items-center justify-center p-0 cursor-zoom-in group/canvas relative"
                                                    onClick={() => {
                                                        const imgToZoom = viewMode === '3d' ? effective3D : effective2D;
                                                        if (imgToZoom) setZoomedImage(getImageUrl(imgToZoom));
                                                    }}
                                                >
                                                    {((viewMode === '3d' && effective3D) || (viewMode === '2d' && effective2D)) ? (
                                                        <img
                                                            src={getImageUrl(viewMode === '3d' ? effective3D : effective2D)}
                                                            className="w-full h-full object-contain transition-transform duration-700 group-hover/canvas:scale-105"
                                                            alt={`${currentUnit.unitType} ${viewMode.toUpperCase()} Floor Plan`}
                                                            key={`${activeTab}-${activeSubTab}-${viewMode}`}
                                                        />
                                                    ) : (
                                                        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                                                            <i className="fa-solid fa-image-slash text-5xl mb-4 opacity-20"></i>
                                                            <p className="text-sm font-bold opacity-60">No {viewMode.toUpperCase()} Floor Plan Image Available</p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* UI Controls (Lower Right Overlay) */}
                                            {viewMode !== 'virtual' && (
                                                <div className="absolute bottom-4 right-4 z-20 flex flex-col gap-2">
                                                    <button 
                                                        type="button"
                                                        onClick={() => setViewMode('2d')}
                                                        title="Reset to 2D Plan"
                                                        className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-white rounded-xl shadow-lg border border-gray-100 text-black active:scale-95 transition-all"
                                                    >
                                                        <i className="fa-solid fa-rotate-left text-xs sm:text-sm text-[#1e3a8a]"></i>
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>

                            {/* Bottom Room Metrics Table */}
                            {currentSubUnit.details && currentSubUnit.details.length > 0 && (
                                <div className="pl-0 pr-4 md:pl-0 md:pr-6 mb-8 mt-2 flex justify-start">
                                    <div className="overflow-hidden border border-black/10 rounded-xl shadow-sm bg-white w-full max-w-[350px]">
                                        <table className="w-full text-left border-collapse">
                                            <tbody className="divide-y divide-black/10">
                                                {currentSubUnit.details.map((det: any, dIdx: number) => (
                                                    <tr key={dIdx} className="hover:bg-black/[0.02] transition-colors group">
                                                        <td className="px-4 py-2 border-r border-black/5">
                                                            <div className="flex items-center gap-2.5">
                                                                <div className="w-1.5 h-1.5 rounded-full bg-black/30 group-hover:bg-black transition-all"></div>
                                                                <span className="text-[14px] font-bold text-black">{det.label}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-2 text-[15px] font-black text-black">
                                                            {det.value}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}


                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FloorPlansSection;

